// frontend/src/pages/dashboards/accessState.js
// Phase 4 step 14d-3: who can open a dashboard — the rules of the Share
// dialog's "Who can open this dashboard" and "People" sections, kept pure so
// they are tested without a browser (accessState.test.js). The dialog
// (ShareDialog.jsx, AccessSections.jsx) only draws what these return.
//
// The server's listing (backend app/dashboards/access_routes.py `listing`) is
//   { dashboard_id, access: 'org' | 'restricted', organization_name, public_links,
//     permissions: { view, edit, share }, filters: [{ id, label, kind }],
//     people: [person], guests: { active, max, org_active, org_max },
//     resend_cooldown_minutes, person? }
// and a person is
//   { id, kind: 'member' | 'guest', status: 'member' | 'not_sent' | 'invited' | 'opened',
//     user: { id, email, role, can_be_editor } (member) | null, email (guest) | null,
//     can_edit, filter_ids (guest: null = every filter), granted_by, granted_at,
//     invited_at, view_count, last_viewed_at, is_you, mail_sent? (add / resend) }
// accessFromServer keeps only what has that shape, so the dialog never draws
// something it did not expect.
import { agoText } from './shareState';

export const LEVELS = ['org', 'restricted'];
export const MAX_EMAIL = 254;                 // access_routes.MAX_EMAIL
export const MAX_SUGGESTIONS = 8;             // shown under the Add box (the server sends up to 50)
const DEFAULT_GUEST_MAX = 25;                 // access_routes.MAX_GUESTS_PER_DASHBOARD
const DEFAULT_ORG_GUEST_MAX = 200;            // access_routes.MAX_GUESTS_PER_ORG
const DEFAULT_COOLDOWN_MIN = 15;              // access_routes.RESEND_COOLDOWN_MIN
// The server's rule (access_routes.EMAIL_RE): one address, a dotted domain, no empty label, no spaces or <>.
const EMAIL_RE = /^[^\s@<>]+@(?:[^\s@<>.]+\.)+[^\s@<>.]+$/;
const CONTROL = /[\x00-\x1f\x7f]/;            // eslint-disable-line no-control-regex

export const ROLE_LABELS = { admin: 'Admin', analyst: 'Analyst', viewer: 'Viewer' };

// --- the server's listing -------------------------------------------------------------

const text = (v) => (typeof v === 'string' ? v : null);
const count = (v) => (Number.isInteger(v) && v >= 0 ? v : 0);
const positive = (v, fallback) => (Number.isInteger(v) && v > 0 ? v : fallback);
const ids = (v) => (Array.isArray(v) ? v.filter((x) => typeof x === 'string') : null);

export function personFromServer(p) {
  if (!p || !Number.isInteger(p.id) || p.id <= 0) return null;
  const kind = p.kind === 'member' || p.kind === 'guest' ? p.kind : null;
  if (!kind) return null;
  let user = null;
  if (kind === 'member') {
    if (!p.user || !Number.isInteger(p.user.id) || p.user.id <= 0) return null;
    const role = ROLE_LABELS[p.user.role] ? p.user.role : null;
    user = { id: p.user.id, email: text(p.user.email) || '', role, canBeEditor: p.user.can_be_editor === true };
  }
  const email = kind === 'member' ? user.email : (text(p.email) || '').toLowerCase();
  if (kind === 'guest' && !email) return null;
  let status = 'member';
  if (kind === 'guest') status = ['not_sent', 'invited', 'opened'].includes(p.status) ? p.status : 'invited';
  return {
    id: p.id,
    kind,
    status,
    email,
    userId: user ? user.id : null,
    role: user ? user.role : null,
    canBeEditor: user ? user.canBeEditor : false,
    canEdit: kind === 'member' && p.can_edit === true,
    filterIds: kind === 'guest' ? ids(p.filter_ids) : null,       // null = every filter
    grantedBy: text(p.granted_by),
    grantedAt: text(p.granted_at),
    invitedAt: text(p.invited_at),
    viewCount: count(p.view_count),
    lastViewedAt: text(p.last_viewed_at),
    isYou: kind === 'member' && p.is_you === true,
    mailSent: p.mail_sent === true ? true : p.mail_sent === false ? false : null,
  };
}

export function accessFromServer(body) {
  const b = body && typeof body === 'object' ? body : {};
  const perms = b.permissions && typeof b.permissions === 'object' ? b.permissions : {};
  const g = b.guests && typeof b.guests === 'object' ? b.guests : {};
  return {
    access: b.access === 'restricted' ? 'restricted' : 'org',
    organization: (text(b.organization_name) || '').trim() || 'your organization',
    publicLinks: count(b.public_links),
    permissions: { edit: perms.edit === true, share: perms.share === true },
    filters: (Array.isArray(b.filters) ? b.filters : [])
      .filter((f) => f && typeof f.id === 'string' && f.id)
      .map((f) => ({ id: f.id, label: text(f.label) || 'Filter', kind: text(f.kind) || 'values' })),
    people: (Array.isArray(b.people) ? b.people : []).map(personFromServer).filter(Boolean),
    guests: {
      active: count(g.active), max: positive(g.max, DEFAULT_GUEST_MAX),
      orgActive: count(g.org_active), orgMax: positive(g.org_max, DEFAULT_ORG_GUEST_MAX),
    },
    resendCooldownMinutes: positive(b.resend_cooldown_minutes, DEFAULT_COOLDOWN_MIN),
    person: b.person ? personFromServer(b.person) : null,
  };
}

export const memberRows = (access) => (access ? access.people.filter((p) => p.kind === 'member') : []);
export const guestRows = (access) => (access ? access.people.filter((p) => p.kind === 'guest') : []);

/** The Add box's picker (GET …/access/people): { people: [{ id, email, role, canBeEditor, shared }], truncated }. */
export function suggestionsFromServer(body) {
  const b = body && typeof body === 'object' ? body : {};
  const people = (Array.isArray(b.people) ? b.people : [])
    .filter((u) => u && Number.isInteger(u.id) && u.id > 0 && typeof u.email === 'string' && u.email)
    .map((u) => ({
      id: u.id, email: u.email, role: ROLE_LABELS[u.role] ? u.role : null,
      canBeEditor: u.can_be_editor === true, shared: u.shared === true,
    }));
  return { people, truncated: b.truncated === true };
}

/** The suggestions to show: at most MAX_SUGGESTIONS, each marked `added` when already on the dashboard. */
export function shownSuggestions(people, access) {
  const on = new Set(memberRows(access).map((m) => m.userId));
  return (people || []).slice(0, MAX_SUGGESTIONS).map((u) => ({ ...u, added: u.shared || on.has(u.id) }));
}

/** What the page needs from GET /dashboards/{id}: may this person change it, may they share it. */
export function pagePermissions(dashboard) {
  if (!dashboard) return { edit: false, share: false };
  const p = dashboard.permissions;
  if (p && typeof p === 'object') return { edit: p.edit === true, share: p.share === true };
  const edit = dashboard.can_edit === true;          // a server from before 14d: can_edit was the only answer
  return { edit, share: edit };
}

// --- the level ----------------------------------------------------------------------------

export function levelOptions(organization) {
  return [
    {
      id: 'org',
      label: `People in ${organization}`,
      hint: `Everyone in ${organization} can find and open it. Admins and analysts can edit it.`,
    },
    {
      id: 'restricted',
      label: 'Only specific people',
      hint: 'Only admins and the people you add below can find and open it. It can’t have public links.',
    },
  ];
}

export const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** The brief's sentence before a restriction that revokes links (decision 4). */
export function restrictPrompt(links) {
  const n = Math.max(1, count(links));
  const them = n === 1 ? 'it' : 'them';
  return `This dashboard has ${plural(n, 'public link')}. Restricting it revokes ${them}; anyone using ${them} loses access.`;
}

/**
 * What choosing a level does: nothing (already that level), ask first (restricting a dashboard with working
 * public links), or send the change. `links` is the number of working links as the dialog knows it now.
 */
export function levelRequest(current, target, links) {
  if (!LEVELS.includes(target) || target === current) return { action: 'none' };
  if (target === 'restricted' && count(links) > 0) return { action: 'confirm', links: count(links) };
  return { action: 'put' };
}

/** The line under the dialog's title: "People in Acme · 2 guests · 1 public link". */
export function summaryLine(access, links) {
  if (!access) return '';
  const parts = [];
  if (access.access === 'restricted') {
    parts.push('Only specific people');
    const n = memberRows(access).length;
    parts.push(n ? plural(n, 'person', 'people') : 'nobody named yet');
  } else {
    parts.push(`People in ${access.organization}`);
  }
  const g = guestRows(access).length;
  if (g) parts.push(plural(g, 'guest'));
  const l = access.access === 'restricted' ? 0 : count(links);
  if (l) parts.push(plural(l, 'public link'));
  return parts.join(' · ');
}

export function levelNotice(access, revoked = 0) {
  if (access.access === 'restricted') {
    const gone = revoked ? ` ${plural(revoked, 'public link')} ${revoked === 1 ? 'was' : 'were'} revoked.` : '';
    return `Only admins and the people below can open this dashboard now.${gone}`;
  }
  return `Everyone in ${access.organization} can open this dashboard now.`;
}

// --- adding someone -----------------------------------------------------------------------

export const normalizeEmail = (value) => String(value ?? '').trim().toLowerCase();

/** One address the server will take (the same rule as access_routes.normalize_email). */
export function isEmail(value) {
  const s = normalizeEmail(value);
  return Boolean(s) && s.length <= MAX_EMAIL && !CONTROL.test(s) && EMAIL_RE.test(s);
}

/**
 * What the Add box's text is, given the suggestions the server sent for it:
 *   { kind: 'empty' }                  nothing typed
 *   { kind: 'member', user }           a colleague, on a restricted dashboard: add them
 *   { kind: 'inside', email }          a colleague on an organization-level dashboard: they can already open it
 *   { kind: 'already', email }         this person is on the dashboard already
 *   { kind: 'guest', email }           an address outside the organization: invite them as a guest
 *   { kind: 'incomplete' }             not an address and nobody in the list matches it exactly
 * A colleague the suggestions did not include (the list stops at 50) is treated as a guest here; the server
 * answers ALREADY_MEMBER and the dialog offers to add them as a person instead.
 */
export function classifyEntry(value, { suggestions = [], access = null } = {}) {
  const email = normalizeEmail(value);
  if (!email) return { kind: 'empty' };
  const colleague = (suggestions || []).find((u) => normalizeEmail(u.email) === email);
  if (colleague) {
    if (!access || access.access !== 'restricted') return { kind: 'inside', email };
    if (colleague.shared || memberRows(access).some((m) => m.userId === colleague.id)) return { kind: 'already', email };
    return { kind: 'member', user: colleague };
  }
  if (!isEmail(email)) return { kind: 'incomplete' };
  if (memberRows(access).some((m) => normalizeEmail(m.email) === email)) return { kind: 'already', email };
  if (guestRows(access).some((g) => g.email === email)) return { kind: 'already', email };
  return { kind: 'guest', email };
}

/** The sentence under the Add box for a classification that adds nobody, else ''. */
export function entryProblem(entry, access) {
  const org = access ? access.organization : 'your organization';
  const restricted = Boolean(access && access.access === 'restricted');
  if (entry.kind === 'incomplete') return restricted ? 'Pick someone from the list, or type a full email address.' : 'Type a full email address, like name@company.com.';
  if (entry.kind === 'already') return `${entry.email} is already on this dashboard.`;
  if (entry.kind === 'inside') return `${entry.email} is in ${org}, so they can already open this dashboard.`;
  return '';
}

/** A guest can be invited only below both caps; the sentence that says which one is reached, else ''. */
export function guestCapText(access) {
  if (!access) return '';
  const g = access.guests;
  if (g.active >= g.max) return `This dashboard has ${g.max} guests, the most it can have. Remove one to invite someone else.`;
  if (g.orgActive >= g.orgMax) return `${access.organization} has ${g.orgMax} guests across its dashboards, the most it can have. Remove one to invite someone else.`;
  return '';
}

export const memberBody = (user) => ({ kind: 'member', user_id: user.id });

/** A guest's POST body. `filter_ids` is always the ticked list, as for a link: a filter added later stays at its starting value. */
export function guestBody(email, filterIds, filters = []) {
  const ticked = new Set(filterIds || []);
  return { kind: 'guest', email: normalizeEmail(email), filter_ids: filters.map((f) => f.id).filter((id) => ticked.has(id)) };
}

/** The draft of a guest invitation as the Add box opens it: every filter changeable (as a new link). */
export const newGuestDraft = (email, filters = []) => ({ email: normalizeEmail(email), filterIds: filters.map((f) => f.id) });

export function toggleGuestFilter(draft, id) {
  const list = draft.filterIds || [];
  return { ...draft, filterIds: list.includes(id) ? list.filter((x) => x !== id) : [...list, id] };
}

// --- a person's row -------------------------------------------------------------------------

/** The grey line under a person: a member's role; a guest's invitation and use. */
export function statusText(person, now = Date.now()) {
  if (person.kind === 'member') return ROLE_LABELS[person.role] || 'Member';
  if (person.status === 'not_sent') return 'Invitation not sent';
  if (person.status === 'opened') {
    const last = person.lastViewedAt ? `, last ${agoText(person.lastViewedAt, now)}` : '';
    return `Opened ${plural(person.viewCount, 'time')}${last}`;
  }
  const when = agoText(person.invitedAt, now);
  return when ? `Invited ${when}` : 'Invited';
}

/** What a guest may change on the bar: "Can change: Period" / "Can't change any filter"; '' without filters. */
export function guestFiltersText(person, filters) {
  if (!filters || !filters.length || person.kind !== 'guest') return '';
  const list = person.filterIds === null ? filters.map((f) => f.id) : person.filterIds;
  const names = filters.filter((f) => list.includes(f.id)).map((f) => f.label);
  return names.length ? `Can change: ${names.join(', ')}` : "Can't change any filter";
}

/** Whole minutes until this guest's invitation can be sent again (the server's cooldown), 0 when it can now. */
export function resendWait(person, cooldownMinutes = DEFAULT_COOLDOWN_MIN, now = Date.now()) {
  if (person.kind !== 'guest' || person.status === 'not_sent' || !person.invitedAt) return 0;
  const sent = Date.parse(person.invitedAt);
  if (Number.isNaN(sent)) return 0;
  const left = sent + cooldownMinutes * 60000 - now;
  return left > 0 ? Math.ceil(left / 60000) : 0;
}

/**
 * What the row lets the dialog do. `canEdit` toggling: only for a member whose role allows editing, only while
 * the dashboard is restricted (on the organization level every admin and analyst edits anyway), and never on
 * your own row unless you are an admin (unticking it would lock you out of this dialog). Removing yourself
 * from a restricted dashboard is refused by the server unless you are an admin (CANNOT_REMOVE_SELF).
 */
export function rowRights(person, access) {
  const restricted = Boolean(access && access.access === 'restricted');
  const selfLocked = person.isYou && person.role !== 'admin';
  return {
    toggleEdit: person.kind === 'member' && person.canBeEditor && restricted && !selfLocked,
    remove: !(restricted && selfLocked),
    resend: person.kind === 'guest',
    lockedReason: restricted && selfLocked ? 'Another editor or an admin can change your own access.' : '',
  };
}

// --- after a request ------------------------------------------------------------------------

export function addedNotice(person) {
  if (!person) return 'Added.';
  if (person.kind === 'member') return `${person.email} can open this dashboard now.`;
  if (person.mailSent === false) return `${person.email} was added, but the invitation email couldn't be sent. Use Resend to try again.`;
  return `Invitation sent to ${person.email}.`;
}

export function resendNotice(person) {
  if (!person) return 'Invitation sent.';
  if (person.mailSent === false) return `The invitation to ${person.email} couldn't be sent. Their current link still works; try again in a moment.`;
  return `A new invitation went to ${person.email}. The link in the earlier email no longer works.`;
}

export function removedNotice(person) {
  return person.kind === 'guest' ? `${person.email} can no longer open this dashboard.` : `${person.email} was removed.`;
}

export function removePrompt(person) {
  return person.kind === 'guest'
    ? `Remove ${person.email}? Their link stops working at once and they are signed out.`
    : `Remove ${person.email}? They lose access to this dashboard at once.`;
}

const SERVER_SENTENCES = new Set([
  // Codes whose message the server writes for people (whole sentences, nothing from the request in them).
  'CANNOT_RESTRICT', 'CANNOT_REMOVE_SELF', 'GUEST_LIMIT', 'ALREADY_SHARED', 'RESEND_LIMIT', 'ACCESS_REVOKED', 'NOT_A_GUEST',
]);

const FALLBACKS = {
  load: "The sharing settings couldn't be loaded. Try again.",
  level: "That setting couldn't be changed. Try again.",
  add: "This person couldn't be added. Try again.",
  edit: "That change couldn't be saved. Try again.",
  resend: "The invitation couldn't be sent again. Try again.",
  remove: "This person couldn't be removed. Try again.",
};

/** A failed request of the People or level sections, in words. */
export function accessFailureMessage(err, action = 'load') {
  const code = err && err.code;
  if (code === 'PUBLIC_LINKS_ACTIVE') return restrictPrompt(err.links);
  if (SERVER_SENTENCES.has(code) && err.message) return err.message;
  if (code === 'ALREADY_MEMBER') return 'This address belongs to someone in your organization.';
  if (code === 'USER_NOT_FOUND') return "That person isn't an active user in your organization any more.";
  if (code === 'CANNOT_EDIT_ROLE') return 'Only an admin or analyst can be given edit access.';
  if (code === 'INVALID_ACCESS') {
    if (err.path === '$.email') return 'Type one email address, like name@company.com.';
    if (err.path && err.path.startsWith('$.filter_ids')) return 'A filter changed while this was open. Close the dialog and try again.';
    return FALLBACKS[action] || FALLBACKS.load;
  }
  if (code === 'NO_EDIT_ACCESS') return 'You can open this dashboard but not change who can see it.';
  if (err && err.status === 403) return "You don't have permission to share dashboards.";
  if (err && err.status === 404) {
    return ['edit', 'resend', 'remove'].includes(action)
      ? 'This person is no longer on the dashboard, or the dashboard was deleted. Close the dialog and open it again.'
      : 'This dashboard no longer exists.';
  }
  if (code === 'UNAUTHENTICATED') return 'Your session has ended. Sign in again.';
  return FALLBACKS[action] || FALLBACKS.load;
}
